import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ReminderJourneyOverride } from '../../../../generated/event.types';
import { succesToast, errorToast } from 'basics/utils/toast';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const clearEventReminderOverridesMutation = gql`
  mutation clearEventReminderOverrides($eventId: String!) {
    clearEventReminderOverrides(eventId: $eventId) {
      _id
      title
      reminderConfig {
        overrides {
          journeyId
          enabled
        }
      }
    }
  }
`;

type ReminderConfig = {
  overrides: ReminderJourneyOverride[];
};

type EventWithReminderConfig = {
  _id: string;
  title: string;
  reminderConfig: ReminderConfig;
};

type ResultType = {
  clearEventReminderOverrides: EventWithReminderConfig;
};

type UseClearEventReminderOverridesMutation = [
  (eventId: string) => Promise<EventWithReminderConfig | null>,
  MutationResult<ResultType>,
];

export const useClearEventReminderOverridesMutation = (): UseClearEventReminderOverridesMutation => {
  const { t } = useTranslation();
  const [clearEventReminderOverrides, state] = useMutation<ResultType>(clearEventReminderOverridesMutation);

  const executeMutation = useCallback(async (eventId: string) => {
    try {
      const { data } = await clearEventReminderOverrides({
        variables: { eventId },
      });
      succesToast(t('reminder_schedule_reset_success'));

      return data?.clearEventReminderOverrides || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, { eventId });
        Logger.logError({
          error,
          customContext,
          transactionName: 'clearEventReminderOverrides.ts - useClearEventReminderOverridesMutation',
        });
      }
      errorToast(t('reminder_schedule_reset_error'));
      return null;
    }
  }, [clearEventReminderOverrides, t]);

  return [executeMutation, state];
};
